import { EnableLoadingIndicator, EnablePageBlocking, EnableSavingIndicator } from '../../loading-indicator';
import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpParams, HttpResponse } from '@angular/common/http';
import { ParamContext } from '../../service/param.context';
import { Observable } from 'rxjs';
import { SERVER_API_URL } from '../../../app.constants';
import { DatasetModel } from '../model/dataset.model';
import { DatasetTypeEnum } from '../model/dataset-type.enum';
import { createRequestOption } from '../..';
import { ObservationUnitsSearchRequest } from '../model/observation-units-search-request.model';
import { ObservationUnitsSearchResponse } from '../model/observation-units-search-response.model';
import { ObservationVariable } from '../../model/observation-variable.model';
import { DatasetVariable, DatasetVariableOrder } from '../../study/model/dataset-variable';
import { StudyLevelVariableData } from '../model/study-level-variable-data';
import { PlotDatasetProperties } from '../model/plot-dataset-properties.model';
import { ProjectProperties } from '../model/project-properties';
import { StudyInstanceModel } from '../model/study-instance.model';
import { VariableTypeEnum } from '../../ontology/variable-type.enum';
import { ObservationUnitsMetadata } from '../model/observation-units-metadata';
import { ObservationVariableItem } from '../../../study-editor/shared/models/observation-variable-item.model';
import { Skip404Handler, SkipErrorInterceptor } from '../../../blocks/interceptor/error.interceptor';
import { TreatmentVariable } from '../../study/model/treatment-variable.model';
import { ObservationUnitData } from '../model/observation-unit-data.model';

@Injectable()
export class DatasetService {

    constructor(private http: HttpClient,
                private paramContext: ParamContext) {
    }

    generation(studyId: number, measurementDatasetId: number, request: any): Observable<DatasetModel> {
        const url: string = SERVER_API_URL + `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${measurementDatasetId}/generation`;
        return this.http.post<DatasetModel>(url, request, { context: new HttpContext().set(EnableSavingIndicator, true) });
    };

    updateSubObservationUnitsNumberOfUnits(studyId: number, datasetId: number, subObservationUnitsNumberOfUnitsMap: any) {
        const url: string = SERVER_API_URL + `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/update-sub-observation-units`;
        return this.http.patch(url, subObservationUnitsNumberOfUnitsMap, { context: new HttpContext().set(EnableSavingIndicator, true) });
    }

    deleteDataset(studyId: number, datasetId: number) {
        const url: string = SERVER_API_URL + `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}`;
        return this.http.delete<void>(url, { context: new HttpContext().set(EnableSavingIndicator, true) });
    };

    getTemplateStudy(studyId: number) {
        const url: string = SERVER_API_URL + `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/template`;
        return this.http.get<any>(url);
    };

    getDataset(studyId: number, datasetId: number): Observable<HttpResponse<DatasetModel>> {
        return this.http.get<any>(SERVER_API_URL + `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}`,
            { observe: 'response', context: new HttpContext().set(EnableLoadingIndicator, true) });
    }

    getDatasetInstances(studyId: number, datasetId: number): Observable<StudyInstanceModel[]> {
        return this.http.get<StudyInstanceModel[]>(SERVER_API_URL + `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/instances`, { context: new HttpContext().set(EnableLoadingIndicator, true) });
    };

    getSubObservationUnitsNumberOfUnitsMap(studyId: number, datasetId: number): Observable<Map<number, number>>  {
        return this.http.get<Map<number, number>>(SERVER_API_URL + `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/sub-observation-units-number-map`, { context: new HttpContext().set(EnableLoadingIndicator, true) });
    };

    getDatasetsByTypeIds(studyId: number, datasetTypeIds: DatasetTypeEnum[]): Observable<HttpResponse<DatasetModel[]>> {
        const url: string = SERVER_API_URL + `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets?datasetTypeIds=${datasetTypeIds}`;
        return this.http.get<any>(url, { observe: 'response', context: new HttpContext().set(EnableLoadingIndicator, true) });
    }

    getVariablesByVariableType(studyId: number, variableTypeIds: number[]): Observable<HttpResponse<ObservationVariable[]>> {
        const params = createRequestOption(Object.assign({ variableTypeIds }));
        const url: string = SERVER_API_URL + `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/variables/types`;
        return this.http.get<any>(url, { params, observe: 'response', context: new HttpContext().set(EnableLoadingIndicator, true) });
    }

    getVariables(studyId: number, datasetId: number, variableTypeId: number): Observable<HttpResponse<ObservationVariable[]>> {
        const url = SERVER_API_URL + `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/variables/${variableTypeId}`;
        return this.http.get<any>(url, { observe: 'response', context: new HttpContext().set(EnableLoadingIndicator, true) });
    };

    getDatasetVariablesByVariableType(studyId: number, datasetId: number, variableTypeId: number): Observable<HttpResponse<ObservationVariable[]>> {
        const url: string = SERVER_API_URL + `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/variables/${variableTypeId}`;
        return this.http.get<ObservationVariable[]>(url, { observe: 'response', context: new HttpContext().set(EnableLoadingIndicator, true) });
    }

    getDatasets(studyId: number, datasetIds: number[]): Observable<HttpResponse<DatasetModel[]>> {
        // Get datasets with metadata by default
        const params = {};
        const url = SERVER_API_URL + `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets`;
        if (datasetIds) {
            params['datasetTypeIds'] = datasetIds;
        }
        return this.http.get<DatasetModel[]>(url, { params, observe: 'response', context: new HttpContext().set(EnableLoadingIndicator, true) });
    }


    getSubObsMetadata(studyId: number): Observable<SubObsMetadata> {
        return this.http.get<SubObsMetadata>(`${SERVER_API_URL}crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/dataset-metadata`, { context: new HttpContext().set(EnableLoadingIndicator, true) });
    }

    getDatasetsWithoutMetadata(studyId: number, datasetIds: number[]): Observable<HttpResponse<DatasetModel[]>> {
        // Use this method when you want to get datasets without metadata to improve performance and
        // you are only interested in the list of datasets.
        const params = {};
        const url = SERVER_API_URL + `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets`;
        if (datasetIds) {
            params['datasetTypeIds'] = datasetIds;
            params['retrieveMetadata'] = false;
        }
        return this.http.get<DatasetModel[]>(url, { params, observe: 'response', context: new HttpContext().set(EnableLoadingIndicator, true) });
    }

    getObservationSetColumns(studyId: number, datasetId: number, draftMode?: boolean): Observable<HttpResponse<ObservationVariable[]>> {
        const params = {};
        if (draftMode !== undefined) {
            params['draftMode'] = draftMode;
        }
        const url = SERVER_API_URL + `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/observationUnits/table/columns`;
        return this.http.get<ObservationVariable[]>(url, { params, observe: 'response', context: new HttpContext().set(EnableLoadingIndicator, true) });
    }

    getObservationUnitTable(studyId: number,
        datasetId: number,
        request: ObservationUnitsSearchRequest,
        pagination: any): Observable<HttpResponse<ObservationUnitsSearchResponse[]>> {
        const params = createRequestOption(pagination);
        const url = SERVER_API_URL + `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/observationUnits/table`;
        return this.http.post<ObservationUnitsSearchResponse[]>(url, request, { params, observe: 'response', context: new HttpContext().set(EnableLoadingIndicator, true) });
    }

    exportDataset(studyId: number,
        datasetId: number,
        instanceIds: any,
        singleFile: any) {
        const url = SERVER_API_URL + `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/xlsx`;
        return this.http.get(url,
            {
                responseType: 'blob',
                observe: 'response',
                params: {
                    'instanceIds': instanceIds,
                    'collectionOrderId': '1',
                    'singleFile': singleFile,
                    'includeSampleGenotypeValues': 'false'
                },
            }
        );
    };

    exportDatasetNew(studyId: number,
        datasetId: number,
        instanceIds: any,
        collectionOrderId: number,
        singleFile: any,
        selectedFormatType: string,
        includeSampleGenotypeValues: boolean,
        transposeByPlot: boolean,
        isExperimentalDesignDetailsOnly: boolean
    ) {
        const url = SERVER_API_URL + `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/${selectedFormatType}`;

        const params = {};
        if (instanceIds) {
            params['instanceIds'] = instanceIds;
        }
        if (collectionOrderId) {
            params['collectionOrderId'] = collectionOrderId;
        }
        if (includeSampleGenotypeValues) {
            params['includeSampleGenotypeValues'] = includeSampleGenotypeValues;
        }
        if (isExperimentalDesignDetailsOnly) {
            params['isExperimentDesignDetailsOnly'] = isExperimentalDesignDetailsOnly;
        }
        if (transposeByPlot) {
            params['transposeByPlot'] = transposeByPlot;
        }
        params['singleFile'] = singleFile;

        return this.http.get(url,
            {
                responseType: 'blob',
                observe: 'response',
                params,
                context: new HttpContext().set(EnableLoadingIndicator, true)
            }
        );
    };


    getStudyLevelSettings(studyId: number): Observable<ObservationVariable[]> {
        const url = `${SERVER_API_URL}crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/study-details`;
        return this.http.get<ObservationVariable[]>(url, { context: new HttpContext().set(EnableLoadingIndicator, true) });
    }

    addStudyLevelSettings(studyId: number, datasetVariable: DatasetVariable): Observable<HttpResponse<ObservationVariable>> {
        const url: string = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/study-details`;

        return this.http.put<ObservationVariable>(url, datasetVariable, {
            observe: 'response',
            context: new HttpContext().set(EnableSavingIndicator, true)
        });
    }

    removeStudyLevelSettings(studyId: number, variableIds: number[]): Observable<HttpResponse<void>> {
        const url: string = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/study-details`;

        const params = new HttpParams().set('variableIds', variableIds.join(','));

        return this.http.delete<void>(url, {
            observe: 'response',
            params,
            context: new HttpContext().set(EnableSavingIndicator, true)
        });
    }

    updateStudyLevelSetting(studyId: number, studyLevelVariableData: StudyLevelVariableData): Observable<void> {
        const url = `${SERVER_API_URL}crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/variable/study-detail/${studyLevelVariableData.variableId}`;

        return this.http.patch<void>(url, studyLevelVariableData, { context: new HttpContext().set(EnableSavingIndicator, true) });
    }

    updateDatasetProperties(studyId: number, plotDatasetProperties: PlotDatasetProperties) {
        return this.http.put(`${SERVER_API_URL}crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/plot-datasets/properties`, plotDatasetProperties, { context: new HttpContext().set(EnableSavingIndicator, true) });
    };

    addEntryDetails(studyId: number, datasetId: number, datasetVariable: DatasetVariable): Observable<HttpResponse<ObservationVariable>> {
        const url: string = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/entry-details`;

        return this.http.put<ObservationVariable>(url, datasetVariable, {
            observe: 'response',
            context: new HttpContext().set(EnableSavingIndicator, true)
        });
    }

    removeEntryDetails(studyId: number, datasetId: number, variableIds: number[]): Observable<HttpResponse<void>> {
        const url: string = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/entry-details`;
        const params = new HttpParams().set('variableIds', variableIds.join(','));

        return this.http.delete<void>(url, {
            observe: 'response',
            params,
            context: new HttpContext().set(EnableSavingIndicator, true)
        });
    }

    addEnviromentaConditions(studyId: number, datasetId: number, datasetVariable: DatasetVariable): Observable<HttpResponse<ObservationVariable>> {
        const url: string = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/enviromental-conditions`;

        return this.http.put<ObservationVariable>(url, datasetVariable, {
            observe: 'response',
            context: new HttpContext().set(EnableSavingIndicator, true)
        });
    }

    addEnviromentDetails(studyId: number, datasetId: number,datasetVariable: DatasetVariable): Observable<HttpResponse<ObservationVariable>> {
        const url: string = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/environment-details`;

        return this.http.put<ObservationVariable>(url, datasetVariable, {
            observe: 'response',
            context: new HttpContext().set(EnableSavingIndicator, true)
        });
    }

    removeEnviromentDetails(studyId: number, datasetId: number, variableIds: number[]): Observable<HttpResponse<void>> {
        const url: string = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/environment-details`;
        const params = new HttpParams().set('variableIds', variableIds.join(','));

        return this.http.delete<void>(url, {
            observe: 'response',
            params,
            context: new HttpContext().set(EnableSavingIndicator, true)
        });
    }

    removeEnviromentConditions(studyId: number, datasetId: number, variableIds: number[]): Observable<HttpResponse<void>> {
        const url: string = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/enviromental-conditions`;
        const params = new HttpParams().set('variableIds', variableIds.join(','));

        return this.http.delete<void>(url, {
            observe: 'response',
            params,
            context: new HttpContext().set(EnableSavingIndicator, true)
        });
    }

    addTraits(studyId: number, datasetId: number, datasetVariable: DatasetVariable): Observable<HttpResponse<ObservationVariable>> {
        const url: string = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/traits`;

        return this.http.put<ObservationVariable>(url, datasetVariable, {
            observe: 'response',
            context: new HttpContext().set(EnableSavingIndicator, true)
        });
    }

    updateStudyTraitsOrder(studyId: number, datasetId: number, variableId: number, datasetVariableOrder: DatasetVariableOrder) {
        const url: string = SERVER_API_URL +
                `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/variable/study-traits-order/${variableId}`;
        return this.http.patch(url, datasetVariableOrder, { context: new HttpContext().set(EnableSavingIndicator, true) });
    }

    addSelections(studyId: number, datasetId: number,datasetVariable: DatasetVariable): Observable<HttpResponse<ObservationVariable>> {
        const url: string = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/selections`;

        return this.http.put<ObservationVariable>(url, datasetVariable, {
            observe: 'response',
            context: new HttpContext().set(EnableSavingIndicator, true)
        });
    }

    removeDesignDetails(studyId: number, datasetId: number, variableIds: number[]): Observable<HttpResponse<void>> {
        const url: string = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/factors`;
        const params = new HttpParams().set('variableIds', variableIds.join(','));

        return this.http.delete<void>(url, {
            params,
            observe: 'response',
            context: new HttpContext().set(EnableSavingIndicator, true)
        });
    }

    removeTraits(studyId: number, datasetId: number, variableIds: number[]): Observable<HttpResponse<void>> {
        const url: string = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/traits`;
        const params = new HttpParams().set('variableIds', variableIds.join(','));

        return this.http.delete<void>(url, {
            params,
            observe: 'response',
            context: new HttpContext().set(EnableSavingIndicator, true)
        });
    }

    removeSelections(studyId: number, datasetId: number, variableIds: number[]): Observable<HttpResponse<void>> {
        const url: string = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/selections`;
        const params = new HttpParams().set('variableIds', variableIds.join(','));

        return this.http.delete<void>(url, {
            params,
            observe: 'response',
            context: new HttpContext().set(EnableSavingIndicator, true)
        });
    }

    deleteObservation(studyId: number, datasetId: number, observationUnitId: number, observationId: number) {
        const url: string = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/observationUnits/${observationUnitId}/observations/${observationId}`;
        return this.http.delete(url, { context: new HttpContext().set(EnableSavingIndicator, true) });
    };

    addObservation(studyId: number, datasetId: number, observationUnitId: number, observation: any) {
        const url: string = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/observationUnits/${observationUnitId}/observations`;
        return this.http.post(url, observation, { context: new HttpContext().set(EnableSavingIndicator, true) });
    };

    addFactor(studyId: number, datasetId: number, observationUnitId: number, observation: any) {
        const url: string = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/observationUnits/${observationUnitId}/factors`;
        return this.http.post(url, observation, { context: new HttpContext().set(EnableSavingIndicator, true) });
    };

    updateObservation(studyId: number, datasetId: number, observationUnitId: number, observationId: number, observationValue: any): Observable<ObservationUnitData> {
        const url: string = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/observationUnits/${observationUnitId}/observations/${observationId}`;
        return this.http.patch(url, observationValue, { context: new HttpContext().set(EnableSavingIndicator, true) });
    };

    countFilteredPhenotypesAndInstances(studyId: number, datasetId: number, observationUnitsSearchRequest: ObservationUnitsSearchRequest): Observable<any> {
        const url =  SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/observation-units/observations/filter/count`;
        return this.http.post(url, observationUnitsSearchRequest);
    };

    setValueToVariable(studyId: number, datasetId: number, request: any): Observable<any> {
        const url = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/observation-units/filter/set-value`;
        return this.http.post<any>(url, request, { context: new HttpContext().set(EnableSavingIndicator, true).set(EnablePageBlocking, true) });
    }

    deleteVariableValues(studyId: number, datasetId: number, request: any): Observable<any> {
        const url = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/observation-units/filter/delete-value`;
        return this.http.post<any>(url, request, { context: new HttpContext().set(EnableSavingIndicator, true).set(EnablePageBlocking, true) });
    }

    acceptDraftDataByVariable(studyId: number, datasetId: number, request: any) {
        const url = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/observation-units/drafts/filter/acceptance`;
        return this.http.post(url, request, { context: new HttpContext().set(EnableSavingIndicator, true).set(EnablePageBlocking, true) });
    };

    acceptDraftData(studyId: number, datasetId: number, instanceIds?: number[]): Observable<any> {
        const url = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/observation-units/drafts/acceptance${instanceIds ? '?instanceIds=' + instanceIds.join(',') : ''}`;
        return this.http.post(url, null, { context: new HttpContext().set(EnableSavingIndicator, true).set(EnablePageBlocking, true) });
    }

    checkOutOfBoundDraftData(studyId: number, datasetId: number, context?: HttpContext): Observable<any> {
        const url = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/observation-units/drafts/out-of-bounds`;
        const httpContext = context || new HttpContext().set(EnableLoadingIndicator, true).set(Skip404Handler, true);
        return this.http.get(url, { observe: 'response', context: httpContext });
    }

    rejectDraftData(studyId: number, datasetId: number, instanceIds?: number[]): Observable<any> {
        const url = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/observation-units/drafts/rejection${instanceIds ? '?instanceIds=' + instanceIds.join(',') : ''}`;
        return this.http.post(url, null, { context: new HttpContext().set(EnableSavingIndicator, true).set(EnablePageBlocking, true) });
    }

    setAsMissingDraftData(studyId: number, datasetId: number, instanceIds?: number[]): Observable<any> {
        const url = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/observation-units/drafts/set-as-missing${instanceIds ? '?instanceIds=' + instanceIds.join(',') : ''}`;
        return this.http.post(url, null , { context: new HttpContext().set(EnableSavingIndicator, true) });
    }

    getAllProperties(studyId: number, datasetId: number, variableTypeId?: number): Observable<ProjectProperties> {
        const url = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/properties`;
        return  this.http.get<ProjectProperties>(url, { context: new HttpContext().set(EnableLoadingIndicator, true) });
    };

    importAdditionalDesignFactors(studyId: number, datasetId: number, observationList, processWarnings, variablesList: ObservationVariableItem[]) {
        const url = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/observationUnits/factors`;
        return this.http.put(url,
            {
                processWarnings: processWarnings,
                data: observationList,
                draftMode: true,
                optionalDesignVariables: variablesList
            }, { context: new HttpContext().set(EnableSavingIndicator, true).set(EnablePageBlocking, true).set(SkipErrorInterceptor, true) });
    };

    importObservations(studyId: number, datasetId: number, observationList, processWarnings) {
        const url = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/observationUnits/observations`;
        return this.http.put(url,
            {
                processWarnings: processWarnings,
                data: observationList,
                draftMode: true
            }, { context: new HttpContext().set(EnableSavingIndicator, true).set(EnablePageBlocking, true).set(SkipErrorInterceptor, true) });
    };

    importEnvironmentVariableValues(studyId: number, datasetId: number, observationList) {
        const url = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/environment-variables/values`;
        return this.http.put(url,
            {
                data: observationList
            }, { context: new HttpContext().set(EnableSavingIndicator, true).set(EnablePageBlocking, true) });
    };

    importStudySettingVariableValues(studyId: number, observationList) {
        const url = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/study-setting-variables/values`;
        return this.http.put(url,
            {
                data: observationList
            }, { context: new HttpContext().set(EnableSavingIndicator, true).set(EnablePageBlocking, true) });
    };

    addVariables(studyId: number, datasetId: number, datasetVariable: DatasetVariable): Observable<ObservationVariable> {
        let url = '';
        if (datasetVariable.variableTypeId === VariableTypeEnum.STUDY_DETAIL) {
            url = `${SERVER_API_URL}crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/study-details`;
        }
        if (datasetVariable.variableTypeId === VariableTypeEnum.TRAIT) {
            url = `${SERVER_API_URL}crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/traits`;
        } else if (datasetVariable.variableTypeId === VariableTypeEnum.SELECTION_METHOD) {
            url = `${SERVER_API_URL}crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/selections`;
        } else if (datasetVariable.variableTypeId === VariableTypeEnum.ENVIRONMENT_DETAIL) {
            url = `${SERVER_API_URL}crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/environment-details`;
        } else if (datasetVariable.variableTypeId === VariableTypeEnum.ENVIRONMENT_CONDITION) {
            url = `${SERVER_API_URL}crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/enviromental-conditions`;
        } else if (datasetVariable.variableTypeId === VariableTypeEnum.EXPERIMENTAL_DESIGN) {
            url = `${SERVER_API_URL}crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/design-details`;
        }
        return this.http.put<ObservationVariable>(url, datasetVariable, { context: new HttpContext().set(EnableSavingIndicator, true) });
    };

    saveSearchRequest(studyId: number, datasetId: number, request: any) {
        request.studyId = studyId;
        const url = `/bmsapi/crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/observationUnits/search`;
        return this.http.post<any>(url, request);
    };

    observationCount(studyId: number, datasetId: number, variableIds: number[]) {
        if (datasetId && variableIds) {
            const url = `/bmsapi/crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/variables/observations?variableIds=${variableIds.join(',')}`;
            return this.http.head<any>(url, { observe: 'response' });
        }
    };

    updateObservationUnitsEntry(observationUnitEntryReplaceRequest: any, studyId: number, datasetId: number) {
        const url = `/bmsapi/crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/observation-units/entries`;
        return this.http.post(url, observationUnitEntryReplaceRequest, { context: new HttpContext().set(EnableSavingIndicator, true).set(EnablePageBlocking, true) });
    };

    getObservationUnitsMetadata(searchCompositeRequest, studyId: number, datasetId: number) {
        const url = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/observation-units/metadata`;
        return this.http.post<ObservationUnitsMetadata>(url, searchCompositeRequest, { context: new HttpContext().set(EnableLoadingIndicator, true) });
    };

    getObservationForVisualization(studyId: number, datasetId: number, observationUnitsSearch) {
        const url = `/bmsapi/crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/observationUnits/mapList`;
        return this.http.post(url, observationUnitsSearch, { context: new HttpContext().set(EnableLoadingIndicator, true) });
    };

    addExperimentalDesignDetails(studyId: number, datasetId: number,datasetVariable: DatasetVariable): Observable<HttpResponse<ObservationVariable>> {
        const url: string = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/design-details`;

        return this.http.put<ObservationVariable>(url, datasetVariable, {
            observe: 'response',
            context: new HttpContext().set(EnableSavingIndicator, true)
        });
    }

    editTreatmentFactor(studyId: number, data: TreatmentVariable) {
        const url: string = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/treatment-factors`;
        return this.http.patch<any>(url, {
            treatmentVariable: data
        }, { context: new HttpContext().set(EnableSavingIndicator, true).set(EnablePageBlocking, true) });
    }
    addTreatmentFactor(studyId: number, data: TreatmentVariable) {
        const url: string = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/treatment-factors`;
        return this.http.post<any>(url, {
            treatmentVariable: data
        }, { context: new HttpContext().set(EnableSavingIndicator, true).set(EnablePageBlocking, true) });
    }

    deleteTreatmentFactor(studyId: number, variableId: number): Observable<HttpResponse<void>> {
        const url: string = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/treatment-factors/${variableId}`;
        return this.http.delete<void>(url, {
            observe: 'response',
            context: new HttpContext().set(EnableSavingIndicator, true)
        });
    }

    hasExistingDescriptorValues(studyId: number, datasetId: number, variableIds: number[]): Observable<HttpResponse<boolean>> {
        const url: string = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/descriptors`;

        const params = new HttpParams().set('variableIds', variableIds.join(','));

        return this.http.get<boolean>(url, {
            params,
            observe: 'response',
            context: new HttpContext().set(EnableSavingIndicator, true)
        });
    }

    updateVariableAlias(studyId: number, datasetId: number, observationVariable: ObservationVariable) {
        const url: string = SERVER_API_URL +
            `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/datasets/${datasetId}/variable/alias/${observationVariable.termId}?alias=${observationVariable.alias}`;
        return this.http.patch(url, { context: new HttpContext().set(EnableSavingIndicator, true) });
    }
}


export class SubObsMetadata {
    maxSubObservationUnitSets: number;
    maxSubObservationParentUnit: number;
}
